import { queryClient } from "./queryClient"
import { MutationError, type ErrorClassification } from "./MutationError"
import { PrerequisiteFailedError } from "./PrerequisiteFailedError"
import { PartialSuccessError, type PhotoInput } from "./PartialSuccessError"
import type { PhotoRecord } from "@/hooks/useUploadPhoto"

/**
 * After rehydration from localStorage, mutation error objects are plain
 * JSON — they lose their prototype chain, so `instanceof Error` (and
 * therefore all our type-guard functions) returns false.
 *
 * This function walks every errored mutation in the cache and
 * reconstitutes the plain object back into the correct Error subclass
 * so that downstream code (retryNetworkFailedMutations, useEntityErrors,
 * SyncErrorBanner, scopeCascadeRetry, etc.) works correctly.
 *
 * Call once in the PersistQueryClientProvider `onSuccess` callback,
 * after hydration is complete.
 */
export function reconstituteMutationErrors(): void {
  const errored = queryClient.getMutationCache().findAll({
    status: "error",
  })

  for (const mutation of errored) {
    const error = mutation.state.error
    if (!error || error instanceof Error) continue

    const reconstituted = reconstitute(error)
    if (reconstituted) {
      mutation.state.error = reconstituted
      mutation.state.failureReason = reconstituted
    }
  }
}

function reconstitute(error: unknown): Error | null {
  if (typeof error !== "object" || error === null) return null

  const obj = error as Record<string, unknown>

  if (obj.name === "MutationError" && "classification" in obj) {
    return new MutationError({
      message: (obj.message as string) ?? "Unknown error",
      code: (obj.code as string | null) ?? null,
      hint: (obj.hint as string | null) ?? null,
      details: (obj.details as string | null) ?? null,
      classification: obj.classification as ErrorClassification,
    })
  }

  if (obj.name === "PrerequisiteFailedError" && "scopeId" in obj) {
    return new PrerequisiteFailedError(obj.scopeId as string)
  }

  if (obj.name === "PartialSuccessError" && "entityData" in obj) {
    return new PartialSuccessError(
      (obj.message as string) ?? "Partial success",
      obj.entityData,
      (obj.succeededPhotos as { record: PhotoRecord; input: PhotoInput }[]) ??
        [],
      (obj.failedPhotos as { input: PhotoInput; error: unknown }[]) ?? [],
    )
  }

  return null
}
